import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';
import * as fromRoot from '../../../../../../../../../state/app.state';
import {
  CreateContractorRequest,
  UpdateContractorRequest,
  DeleteContractorRequest,
  FetchProjectMembersListRequest,
} from '../../../../../../../core/projects.actions';
import { projectMembersListInfo } from '../../../../../../../core/projects.selectors';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../../shared/services/common.service';
import { AppHttpService } from '../../../../../../../../../shared/services/app-http.service';

@Component({
    selector: 'app-create-or-edit-contractor',
    templateUrl: './create-or-edit-contractor.component.html',
    styleUrls: ['./create-or-edit-contractor.component.scss'],
    standalone: false
})
export class CreateOrEditContractorComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() data: any;

  form = new FormGroup({
    name: new FormControl({ value: null, disabled: false }, { validators: [Validators.required] }),
    is_prime: new FormControl({ value: false, disabled: false }),
    is_dmwbe: new FormControl({ value: false, disabled: false }),
    poc: new FormControl({ value: null, disabled: false }),
    poc_phone: new FormControl({ value: '+1', disabled: false }),
    addressLine1: new FormControl({ value: '', disabled: false }),
    addressLine2: new FormControl({ value: '', disabled: false }),
    addressCity: new FormControl({ value: '', disabled: false }),
    addressRegion: new FormControl({ value: '', disabled: false }),
    addressCountry: new FormControl({ value: '', disabled: false }),
    addressZip: new FormControl({ value: '', disabled: false }),
  });

  // Users tab
  newUserForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    phone: new FormControl(''),
  });
  userSelectForm = new FormGroup({
    selectedUser: new FormControl([]),
  });
  showNewUserForm = false;
  contractorAccounts: any[] = [];
  allMemberOptions: any[] = [];
  memberOptions: any[] = [];
  isEditMode = false;
  selectedTabIndex = 0;

  errorMessages: any = {};
  errorMessageMap = {
    name: {
      required: 'This field is required.',
    },
    poc: {
      required: 'This field is required.',
    },
    poc_phone: {
      required: 'This field is required.',
    },
    addressLine1: {
      required: 'This field is required.',
    },
    addressCity: {
      required: 'This field is required.',
    },
    addressRegion: {
      required: 'This field is required.',
    },
    addressCountry: {
      required: 'This field is required.',
    },
    addressZip: {
      required: 'This field is required.',
    },
  };
  isFormDirty = false;
  projectId: any;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private dialog: MatDialog,
    private appHttpService: AppHttpService,
    private cdRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
    } else {
      const url = this.router.url;
      const path = url.split('?')[0];
      const segments = path.split('/');
      this.projectId = segments[segments.length - 1];
    }

    if (!this.projectId && this.data?.projectId) {
      this.projectId = this.data?.projectId;
    }

    this.isEditMode = !!(this.data?.id || this.data?.uuid);

    if (this.isEditMode || this.data?.name) {
      let phoneValue = this.data?.poc_phone || '+1';
      if (phoneValue && !phoneValue.startsWith('+')) {
        phoneValue = '+1' + phoneValue;
      } else if (!phoneValue) {
        phoneValue = '+1';
      }

      this.form.patchValue({
        name: this.data?.name || null,
        is_prime: this.data?.prime ?? false,
        is_dmwbe: this.data?.source_entity_extra_attributes?.dmwbe === 'Y',
        poc: this.data?.poc || null,
        poc_phone: phoneValue,
        addressLine1: this.data?.address?.line1 || '',
        addressLine2: this.data?.address?.line2 || '',
        addressCity: this.data?.address?.city || '',
        addressRegion: this.data?.address?.region || '',
        addressCountry: this.data?.address?.country || '',
        addressZip: this.data?.address?.zip || '',
      });

      setTimeout(() => {
        this.updateAddressValidation();
      });
    }

    // Load contractor accounts and project members when editing
    if (this.isEditMode) {
      this.loadContractorAccounts();

      this.store.dispatch(FetchProjectMembersListRequest({ payload: { projectId: this.projectId } }));

      this.store.select(projectMembersListInfo).pipe(takeUntil(this.onDestroy)).subscribe(data => {
        if (data?.list) {
          this.allMemberOptions = data.list
            .filter(m => (m.role_code || m.role || '').startsWith('contractor'))
            .map(m => ({
              value: m.uuid || m.id,
              label: `${m.first_name} ${m.last_name}` + (m.email ? ` (${m.email})` : ''),
              first_name: m.first_name,
              last_name: m.last_name,
              email: m.email,
            }));
          this.updateMemberOptions();
        }
      });

      this.userSelectForm.get('selectedUser').valueChanges
        .pipe(takeUntil(this.onDestroy))
        .subscribe(vals => {
          if (!Array.isArray(vals)) return;
          for (const val of vals) {
            if (val === 'ADD_NEW') continue;
            const member = this.memberOptions.find(m => m.value === val);
            if (member && !this.contractorAccounts.find(a => a.account_id === member.value)) {
              this.addExistingUser(member);
            }
          }
        });
    }

    this.projectsService.isAnyFormDirty.pipe(takeUntil(this.onDestroy)).subscribe(dirty => {
      this.isFormDirty = dirty;
    });

    this.form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.isFormDirty = true;
      this.projectsService.isAnyFormDirty.next(true);
      this.updateAddressValidation();
    });

    this.updateAddressValidation();
  }

  ngAfterViewInit(): void {
    this.projectsService.isAnyFormDirty.next(false);
  }

  onToggle(event: MatSlideToggleChange): void {
    this.form.controls['is_prime'].setValue(event?.checked);
  }

  onDmwbeToggle(event: MatSlideToggleChange): void {
    this.form.controls['is_dmwbe'].setValue(event?.checked);
  }

  updateAddressValidation(): void {
    const values = this.form.getRawValue();
    const hasValue = (val: any): boolean => {
      return val && val.toString().trim().length > 0;
    };

    const hasAnyAddressValue =
      hasValue(values?.addressLine1) ||
      hasValue(values?.addressLine2) ||
      hasValue(values?.addressCity) ||
      hasValue(values?.addressRegion) ||
      hasValue(values?.addressCountry) ||
      hasValue(values?.addressZip);

    const addressFields = ['addressLine1', 'addressCity', 'addressRegion', 'addressCountry', 'addressZip'];

    if (hasAnyAddressValue) {
      addressFields.forEach(field => {
        const control = this.form.get(field);
        if (control) {
          control.setValidators([Validators.required]);
          control.updateValueAndValidity({ emitEvent: false });
        }
      });
    } else {
      addressFields.forEach(field => {
        const control = this.form.get(field);
        if (control) {
          control.clearValidators();
          control.setErrors(null);
          control.updateValueAndValidity({ emitEvent: false });
        }
        delete this.errorMessages[field];
      });
      const line2Control = this.form.get('addressLine2');
      if (line2Control) {
        line2Control.clearValidators();
        line2Control.setErrors(null);
        line2Control.updateValueAndValidity({ emitEvent: false });
      }
      delete this.errorMessages['addressLine2'];
    }
  }

  submit(): void {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    const values = this.form.getRawValue() as any;
    let payload: any;

    const hasAddress =
      values?.addressLine1 ||
      values?.addressLine2 ||
      values?.addressCity ||
      values?.addressRegion ||
      values?.addressCountry ||
      values?.addressZip;
    const addressPayload = hasAddress
      ? {
          line1: values?.addressLine1 || null,
          line2: values?.addressLine2 || null,
          city: values?.addressCity || null,
          region: values?.addressRegion || null,
          country: values?.addressCountry || null,
          zip: values?.addressZip || null,
        }
      : null;

    let formattedPhone = values?.poc_phone || '';
    if (formattedPhone && !formattedPhone.startsWith('+')) {
      formattedPhone = '+1' + formattedPhone;
    } else if (!formattedPhone || formattedPhone === '+1') {
      formattedPhone = null;
    }

    if (this.data?.id || this.data?.uuid) {
      const contractorId = this.data?.id || this.data?.uuid;
      const updateBody: any = {
        id: contractorId,
        name: values?.name,
        prime: !!values?.is_prime,
        dmwbe: values?.is_dmwbe ? 'Y' : 'N',
        ...(values?.poc && { poc: values.poc }),
        ...(formattedPhone && { poc_phone: formattedPhone }),
      };

      if (addressPayload) {
        updateBody.address = addressPayload;
      }

      payload = {
        projectId: this.projectId,
        contractorId: contractorId,
        body: updateBody,
      };

      this.store.dispatch(UpdateContractorRequest({ payload }));
      return;
    } else {
      const createBody: any = {
        name: values?.name,
        prime: !!values?.is_prime,
        dmwbe: values?.is_dmwbe ? 'Y' : 'N',
        ...(values?.poc && { poc: values.poc }),
        ...(formattedPhone && { poc_phone: formattedPhone }),
      };

      if (addressPayload) {
        createBody.address = addressPayload;
      }

      payload = {
        projectId: this.projectId,
        body: createBody,
      };

      this.store.dispatch(CreateContractorRequest({ payload }));
      return;
    }
  }

  checkForErrors(currentField?: string): void {
    const validationErrors = this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField);
    if (validationErrors) {
      this.errorMessages = {
        ...this.errorMessages,
        ...validationErrors,
      };
    }
  }

  deleteContractor(): void {
    const contractorName = this.data?.name || 'this contractor';
    const message = `Deleting <b>${contractorName}</b> cannot be undone.<br />Proceed to delete?`;
    const dialogData = new ConfirmDialogModel('Delete?', message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const payload = {
          projectId: this.projectId,
          contractorId: this.data?.id || this.data?.uuid,
        };
        this.store.dispatch(DeleteContractorRequest({ payload }));
      }
    });
  }

  // --- Users tab methods ---

  get contractorId(): string {
    return this.data?.id || this.data?.uuid;
  }

  loadContractorAccounts(): void {
    this.appHttpService.getService(
      `/v1/warranty/project/${this.projectId}/contractors/${this.contractorId}/accounts`
    ).subscribe((resp: any) => {
      if (resp?.data) {
        this.contractorAccounts = resp.data.map(a => ({
          account_id: a.account_id || a.uuid || a.id,
          first_name: a.first_name,
          last_name: a.last_name,
          email: a.email,
          role: a.role || 'contractor',
        }));
        this.updateMemberOptions();
      }
    });
  }

  updateMemberOptions(): void {
    const assignedUuids = new Set(this.contractorAccounts.map(a => a.account_uuid));
    const assignedEmails = new Set(this.contractorAccounts.map(a => (a.email || '').toLowerCase()).filter(Boolean));
    this.memberOptions = this.allMemberOptions.filter(m =>
      !assignedUuids.has(m.value) && !assignedEmails.has((m.email || '').toLowerCase())
    );
    this.cdRef.detectChanges();
  }

  addExistingUser(member: any): void {
    this.appHttpService.postService(
      `/v1/warranty/project/${this.projectId}/contractors/${this.contractorId}/accounts`,
      { account_id: member.value, role: 'contractor' }
    ).subscribe((resp: any) => {
      const newAccount = resp?.data || {};
      this.contractorAccounts.push({
        account_id: newAccount.account_id || member.value,
        account_uuid: newAccount.account_uuid || member.value,
        first_name: newAccount.first_name || member.first_name,
        last_name: newAccount.last_name || member.last_name,
        email: newAccount.email || member.email,
        role: newAccount.role || 'contractor',
      });
      this.updateMemberOptions();
      this.userSelectForm.get('selectedUser').setValue([], { emitEvent: false });
    });
  }

  removeContractorUser(account: any): void {
    this.appHttpService.deleteService(
      `/v1/warranty/project/${this.projectId}/contractors/${this.contractorId}/accounts/${account.account_id}`
    ).subscribe(() => {
      this.contractorAccounts = this.contractorAccounts.filter(a => a.account_id !== account.account_id);
      this.updateMemberOptions();
    });
  }

  toggleNewUserForm(): void {
    this.showNewUserForm = !this.showNewUserForm;
  }

  addNewUser(): void {
    if (!this.newUserForm.valid) return;
    const val = this.newUserForm.value;

    this.appHttpService.postService(
      `/v1/warranty/project/${this.projectId}/contractors/${this.contractorId}/accounts/create-user`,
      {
        first_name: val.firstName,
        last_name: val.lastName,
        email: val.email,
        ...(val.phone ? { phone: val.phone } : {}),
      }
    ).subscribe((resp: any) => {
      if (resp?.data) {
        this.contractorAccounts.push({
          account_id: resp.data.account_id || resp.data.uuid || resp.data.id,
          first_name: resp.data.first_name,
          last_name: resp.data.last_name,
          email: resp.data.email,
          role: resp.data.role || 'contractor',
        });
        this.updateMemberOptions();
      }
      this.newUserForm.reset();
      this.showNewUserForm = false;
    });
  }

  ngOnDestroy(): void {
    this.projectsService.isAnyFormDirty.next(false);
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
